import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, CheckCheck } from "lucide-react";
import { cn } from "@/lib/utils";

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "ahora";
  if (s < 3600) return `hace ${Math.floor(s / 60)} min`;
  if (s < 86400) return `hace ${Math.floor(s / 3600)} h`;
  return `hace ${Math.floor(s / 86400)} d`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/notifications"); setData(r.data); } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const onItem = async (n) => {
    if (!n.read) { try { await api.post(`/notifications/${n.id}/read`); } catch (e) { /* noop */ } }
    setOpen(false);
    if (n.link) navigate(n.link);
    load();
  };

  const markAll = async () => { try { await api.post("/notifications/read-all"); load(); } catch (e) { /* noop */ } };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative text-muted-foreground" data-testid="notif-bell-btn">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span data-testid="notif-unread-badge" className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-destructive text-white text-[10px] font-bold flex items-center justify-center">
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-heading font-bold text-sm">Notificaciones</span>
          {data.unread > 0 && (
            <button onClick={markAll} className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="notif-mark-all-btn">
              <CheckCheck className="h-3.5 w-3.5" /> Marcar leídas
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Sin notificaciones</div>
          ) : data.items.map((n) => (
            <button key={n.id} onClick={() => onItem(n)} data-testid={`notif-item-${n.id}`}
              className={cn("w-full text-left px-4 py-3 border-b border-border/60 hover:bg-muted/50 transition-colors duration-200", !n.read && "bg-primary/[0.04]")}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="h-2 w-2 rounded-full bg-primary mt-1.5 shrink-0" />}
                <div className={cn("min-w-0 flex-1", n.read && "pl-4")}>
                  <div className="text-sm font-medium">{n.title}</div>
                  {n.body && <div className="text-xs text-muted-foreground mt-0.5">{n.body}</div>}
                  <div className="text-[11px] text-muted-foreground/70 mt-1">{timeAgo(n.created_at)}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
